"use client"

// Case page body — tension → move → outcome, anonymized (policy §5).
// Register map: the narrative is PAPER (testimony); the metric wall is a DARK
// instrument room (measurement). Naming discipline (hard rule #6): name the
// strategy shift, not deliverables; client names never render without consent.

import Link from "next/link"
import { CtaBlock, InstrumentRoom } from "@/components/v3/site-chrome"
import { Counter, Eyebrow, FadeUp, ZH_SERIF, dark, paper } from "@/components/v3/primitives"
import { cases, casesNote } from "@/lib/proto/content"

const STAGES = [
  { key: "tension", zh: "張力", en: "Tension" },
  { key: "move", zh: "行動", en: "The move" },
  { key: "outcome", zh: "結果", en: "Outcome" },
] as const

export function CaseBody({ caseId }: { caseId: string }) {
  const c = cases.find((x) => x.id === caseId)
  if (!c) return null

  return (
    <>
      {/* ── Header — the strategy shift IS the title ─────────────────────── */}
      <section aria-label="案例" className="relative mx-auto max-w-6xl px-6 pb-16 pt-28 md:px-10 md:pb-24 md:pt-36">
        <FadeUp>
          <Eyebrow zh="案例" en="Case" />
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span
              lang="zh-Hant"
              className="text-[13px] tracking-[0.1em]"
              style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}
            >
              {c.client.zh}
            </span>
            <span className="h-px w-6" style={{ background: paper.hairline }} aria-hidden />
            <span
              lang="zh-Hant"
              className="text-[12px] tracking-[0.1em]"
              style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}
            >
              {c.industry.zh}
            </span>
            <span
              className="rounded-full px-3 py-1 text-[10.5px] tracking-[0.14em]"
              style={{
                fontFamily: "var(--proto-mono)",
                border: `1px solid ${paper.hairline}`,
                color: paper.inkFaint,
              }}
            >
              {c.frame.zh}
            </span>
          </div>
        </FadeUp>
        <FadeUp delay={0.08}>
          <h1
            lang="zh-Hant"
            className="mt-10 max-w-4xl text-balance text-4xl leading-[1.3] md:text-[3.4rem] md:leading-[1.25]"
            style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 620", color: paper.ink }}
          >
            {c.shift.zh}
          </h1>
          <p
            className="mt-5 max-w-3xl text-[14px] italic"
            style={{ fontFamily: "var(--proto-fraunces)", color: paper.inkFaint }}
          >
            {c.shift.en}
          </p>
        </FadeUp>
      </section>

      {/* ── Tension → Move → Outcome ─────────────────────────────────────── */}
      <section
        aria-label="張力、行動、結果"
        className="relative py-20 md:py-28"
        style={{
          background: paper.bgDeep,
          borderTop: `1px solid ${paper.hairlineSoft}`,
          borderBottom: `1px solid ${paper.hairlineSoft}`,
        }}
      >
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <ol className="space-y-16 md:space-y-20">
            {STAGES.map((stage, i) => (
              <FadeUp as="li" key={stage.key} delay={i * 0.05}>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-[10rem_minmax(0,1fr)] md:gap-12">
                  <div>
                    <span className="text-sm" style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}>
                      0{i + 1}
                    </span>
                    <h2
                      lang="zh-Hant"
                      className="mt-2 text-2xl"
                      style={{
                        fontFamily: ZH_SERIF,
                        fontVariationSettings: "'wght' 620",
                        color: paper.accent,
                      }}
                    >
                      {stage.zh}
                    </h2>
                    <p
                      className="mt-1 text-[11px] tracking-[0.18em]"
                      style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}
                    >
                      {stage.en.toUpperCase()}
                    </p>
                  </div>
                  <div className="max-w-3xl">
                    <p
                      lang="zh-Hant"
                      className="text-lg leading-[1.95] md:text-xl md:leading-[1.9]"
                      style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
                    >
                      {c[stage.key].zh}
                    </p>
                    <p
                      className="mt-4 pl-5 text-[13px] leading-relaxed"
                      style={{
                        fontFamily: "var(--proto-hanken)",
                        borderLeft: `1px solid ${paper.hairline}`,
                        color: paper.inkFaint,
                      }}
                    >
                      {c[stage.key].en}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Metrics — the instrument room reads the outcome ──────────────── */}
      <section aria-label="成果數字" className="relative mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-28">
        <FadeUp>
          <InstrumentRoom>
            <dl className="grid grid-cols-2 gap-px md:grid-cols-4" style={{ background: dark.border }}>
              {c.metrics.map((m) => (
                <div key={m.label.en} className="p-6 md:p-8" style={{ background: dark.bg }}>
                  <dt
                    lang="zh-Hant"
                    className="text-[12px] leading-tight"
                    style={{ fontFamily: "var(--proto-mono)", color: dark.soft }}
                  >
                    {m.label.zh}
                  </dt>
                  <dd
                    className="mt-3 text-3xl font-light md:text-4xl"
                    style={{ fontFamily: "var(--proto-fraunces)", color: dark.accent }}
                  >
                    <Counter value={m.value} />
                  </dd>
                </div>
              ))}
            </dl>
          </InstrumentRoom>
        </FadeUp>

        {/* Testimonial — name withheld pending consent (policy §5) */}
        {c.testimonial && (
          <FadeUp delay={0.1}>
            <blockquote className="mx-auto mt-16 max-w-3xl text-center">
              <p
                className="text-balance text-xl leading-[1.7] md:text-2xl"
                style={{ fontFamily: "var(--proto-fraunces)", fontStyle: "italic", color: paper.ink }}
              >
                “{c.testimonial.quote}”
              </p>
              <footer
                className="mt-6 text-[12px] tracking-[0.16em]"
                style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}
              >
                {c.testimonial.position.toUpperCase()} ·{" "}
                <span lang="zh-Hant">具名待客戶同意</span>
              </footer>
            </blockquote>
          </FadeUp>
        )}

        {/* Honesty note — tiered anonymity as a solved operating model */}
        <FadeUp delay={0.15}>
          <p
            lang="zh-Hant"
            className="mx-auto mt-16 max-w-2xl text-center text-[13px] leading-[1.9]"
            style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
          >
            {casesNote.zh}
          </p>
          <p
            className="mx-auto mt-2 max-w-2xl text-center text-[12px] leading-relaxed"
            style={{ fontFamily: "var(--proto-hanken)", color: paper.inkFaint }}
          >
            {casesNote.en}
          </p>
        </FadeUp>

        {/* Cross-link — the method behind the case */}
        <FadeUp delay={0.2}>
          <div className="mt-12 text-center">
            <Link
              href="/brand-engineering"
              lang="zh-Hant"
              className="inline-flex items-center gap-2 text-[13.5px] transition-colors duration-300"
              style={{ fontFamily: "var(--proto-mono)", color: paper.accent }}
            >
              這背後的方法：品牌工程 →
            </Link>
          </div>
        </FadeUp>
      </section>

      <CtaBlock />
    </>
  )
}
